import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Channel, type MetaapiAccount } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { pushToast } from "@/lib/toast";

/**
 * M14 — the MetaApi console mirror. The list is synced server-side on every fetch, so what
 * this page shows IS what the console holds; `stale` says when that sync last failed and the
 * rows are our cached copy.
 */
export function Accounts() {
  const accounts = useQuery({ queryKey: queryKeys.accounts, queryFn: api.accounts });
  const channels = useQuery({ queryKey: queryKeys.channels, queryFn: api.channels });

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Accounts</h1>

      {accounts.isLoading || !accounts.data ? (
        <Skeleton className="h-40 w-full" />
      ) : accounts.isError ? (
        <p className="text-sm text-destructive">Could not load accounts.</p>
      ) : (
        <>
          {accounts.data.stale && (
            <p className="text-xs text-muted-foreground">
              ⚠ The MetaApi console could not be reached — showing the last known state.
            </p>
          )}
          {accounts.data.accounts.map((account) => (
            <AccountCard
              key={account.id}
              account={account}
              channels={channels.data?.channels ?? []}
            />
          ))}
        </>
      )}

      <AddAccountCard />
      <AddVirtualAccountCard />
    </div>
  );
}

const STATE_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  DEPLOYED: "default",
  UNDEPLOYED: "secondary",
  DEPLOYING: "outline",
  UNDEPLOYING: "outline",
};

function useAccountAction<TArgs extends unknown[]>(
  fn: (...args: TArgs) => Promise<unknown>,
  doneMessage: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: TArgs) => fn(...args),
    onSuccess: () => {
      pushToast(doneMessage);
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
    },
    onError: (error) => pushToast(error instanceof Error ? error.message : "Request failed"),
  });
}

export function AccountCard({ account, channels }: { account: MetaapiAccount; channels: Channel[] }) {
  const signalChannels = channels.filter((c) => c.role === "signal" && c.enabled);
  const tradesChannels = channels.filter((c) => c.role === "trades");
  const [confirmRemove, setConfirmRemove] = useState(false);

  const deploy = useAccountAction((id: number) => api.deployAccount(id), "Deploy requested");
  const undeploy = useAccountAction((id: number) => api.undeployAccount(id), "Undeploy requested");
  const activate = useAccountAction(
    (id: number) => api.activateAccount(id),
    "Switching accounts — the app restarts to reconnect",
  );
  const remove = useAccountAction((id: number) => api.removeAccount(id), "Account removed");
  const rateHere = useAccountAction(
    (id: number) => api.setSimulationBase(id),
    "Channels are now rated on this account",
  );
  const stopRating = useAccountAction(
    (id: number) => api.clearSimulationBase(id),
    "Rating stopped on this account",
  );
  const update = useAccountAction(
    (id: number, body: { label?: string | null; tradesChannelId?: number | null }) =>
      api.updateAccount(id, body),
    "Saved",
  );
  const subscribe = useAccountAction(
    (id: number, channelIds: number[]) => api.setAccountSubscriptions(id, channelIds),
    "Subscriptions updated",
  );

  const busy =
    deploy.isPending ||
    undeploy.isPending ||
    activate.isPending ||
    remove.isPending ||
    rateHere.isPending ||
    stopRating.isPending;

  const toggleSubscription = (channelId: number) => {
    const current = new Set(account.subscribedChannelIds);
    if (current.has(channelId)) current.delete(channelId);
    else current.add(channelId);
    subscribe.mutate([account.id, [...current]]);
  };

  // M29 — no MetaApi account exists behind a virtual row, so every console-facing control
  // disappears rather than offering a button that can only 409.
  const virtual = account.metaapiId.startsWith("virtual-");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>{account.label ?? account.name ?? account.metaapiId}</span>
          {account.isActive && <Badge>ACTIVE</Badge>}
          {account.isSimBase && <Badge variant="outline">RATING</Badge>}
          {virtual ? (
            <Badge variant="outline">VIRTUAL</Badge>
          ) : (
            <Badge variant={STATE_VARIANT[account.state] ?? "outline"}>{account.state || "UNKNOWN"}</Badge>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {virtual
            ? "virtual — paper only, no broker connection, no MetaApi cost"
            : `${account.platform || "?"} · ${account.server || "server unknown"} · ${account.connectionStatus || "no connection info"}`}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {!virtual && account.state === "UNDEPLOYED" && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => deploy.mutate([account.id])}>
              Deploy
            </Button>
          )}
          {!virtual && account.state === "DEPLOYED" && !account.isActive && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => undeploy.mutate([account.id])}>
              Undeploy
            </Button>
          )}
          {!virtual && !account.isActive && !account.isSimBase && (
            <Button size="sm" disabled={busy} onClick={() => activate.mutate([account.id])}>
              Activate
            </Button>
          )}
          {/* Not offered on the active account: rating a channel on the account that also
              trades it would measure the trades rather than the channel. The server refuses
              it too — this only keeps Andy from being offered something that cannot work. */}
          {!account.isActive &&
            (account.isSimBase ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => stopRating.mutate([account.id])}>
                Stop rating here
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => rateHere.mutate([account.id])}>
                Rate channels here
              </Button>
            ))}
          {!account.isActive &&
            (confirmRemove ? (
              <>
                <Button size="sm" variant="destructive" disabled={busy} onClick={() => remove.mutate([account.id])}>
                  Really remove
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmRemove(false)}>
                  Keep it
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmRemove(true)}>
                Remove…
              </Button>
            ))}
        </div>

        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Trades notifications go to</label>
          <Select
            value={account.tradesChannelId ?? ""}
            onChange={(e) =>
              update.mutate([
                account.id,
                { tradesChannelId: e.target.value === "" ? null : Number(e.target.value) },
              ])
            }
          >
            <option value="">Default channel</option>
            {tradesChannels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title || c.tgChannelId}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            {account.isSimBase ? "Rates these channels" : "Trades these channels"}
          </label>
          {signalChannels.length === 0 ? (
            <p className="text-xs text-muted-foreground">No enabled signal channels.</p>
          ) : (
            <div className="space-y-1">
              {signalChannels.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={account.subscribedChannelIds.includes(c.id)}
                    disabled={subscribe.isPending}
                    onChange={() => toggleSubscription(c.id)}
                  />
                  {c.title || c.tgChannelId}
                </label>
              ))}
            </div>
          )}
          {account.isSimBase && account.subscribedChannelIds.length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              These fill on paper and place no order. The channel needs auto-execute on to be
              measured — that is safe while it is not also subscribed to the active account.
            </p>
          )}
          {!account.isActive && !account.isSimBase && account.subscribedChannelIds.length > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              Subscriptions take effect when this account is active.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * M29 — the zero-cost row for shadowing. No credentials, no console: one label, one row.
 * Deliberately a separate affordance from "Add account" so nobody mistakes it for a broker
 * account — it can never trade, deploy, or connect.
 */
function AddVirtualAccountCard() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");

  const create = useMutation({
    mutationFn: () => api.createVirtualAccount(label.trim()),
    onSuccess: () => {
      pushToast("Virtual account created");
      setOpen(false);
      setLabel("");
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
    },
    onError: (error) => pushToast(error instanceof Error ? error.message : "Create failed"),
  });

  if (!open) {
    return (
      <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => setOpen(true)}>
        Add virtual account (shadow channels, no MetaApi cost)
      </Button>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add a virtual account</CardTitle>
        <p className="text-xs text-muted-foreground">
          A paper-only row for shadowing channels. Nothing is created at MetaApi and nothing
          is billed — designate it as the rating account and subscribe the channels to watch.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <Input placeholder="Label (e.g. Shadow)" value={label} onChange={(e) => setLabel(e.target.value)} />
        <div className="flex gap-2">
          <Button className="flex-1" disabled={create.isPending || label.trim() === ""} onClick={() => create.mutate()}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AddAccountCard() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    login: "",
    password: "",
    server: "",
    platform: "mt4" as "mt4" | "mt5",
  });

  const create = useMutation({
    mutationFn: () => api.createAccount(form),
    onSuccess: () => {
      pushToast("Account added to MetaApi");
      setOpen(false);
      // The password lives in component state only for the duration of the submit.
      setForm({ name: "", login: "", password: "", server: "", platform: "mt4" });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts });
    },
    onError: (error) => pushToast(error instanceof Error ? error.message : "Create failed"),
  });

  if (!open) {
    return (
      <Button variant="outline" className="w-full" onClick={() => setOpen(true)}>
        Add account
      </Button>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add a broker account to MetaApi</CardTitle>
        <p className="text-xs text-muted-foreground">
          Credentials go straight to MetaApi; nothing is stored here.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <Input placeholder="Name (e.g. main-real)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input placeholder="MT login number" value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} />
        <Input
          type="password"
          placeholder="MT password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
        />
        <Input placeholder="Broker server (e.g. ICMarketsSC-Live24)" value={form.server} onChange={(e) => setForm({ ...form, server: e.target.value })} />
        <Select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value as "mt4" | "mt5" })}>
          <option value="mt4">MT4</option>
          <option value="mt5">MT5</option>
        </Select>
        <div className="flex gap-2">
          <Button
            className="flex-1"
            disabled={create.isPending || !form.name || !form.login || !form.password || !form.server}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Adding…" : "Add"}
          </Button>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
